import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Stack, Divider,
  InputAdornment, CircularProgress,
} from '@mui/material';
import { LockOpen, Storefront, Monitor, CalendarToday } from '@mui/icons-material';
import { useOpenSessionMutation, useLazyListSessionsQuery } from '../../services/cashSessionApi';
import { useToast } from '../common/ToastProvider';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#F8FAFC',
    borderRadius: '8px',
    fontSize: '0.875rem',
    '& fieldset': { borderColor: '#E2E8F0' },
    '&:hover fieldset': { borderColor: '#CBD5E1' },
    '&.Mui-focused fieldset': { borderColor: '#4F46E5' },
  },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4F46E5' },
};

const OpenSessionModal = ({ open, store, terminal, onSessionOpened }) => {
  const showToast = useToast();

  const [openingBalance, setOpeningBalance] = useState('');
  const [notes, setNotes]                   = useState('');
  const [balanceError, setBalanceError]     = useState('');

  const [openSession,        { isLoading: isOpening  }] = useOpenSessionMutation();
  const [triggerListSessions,{ isLoading: isFetching }] = useLazyListSessionsQuery();

  const isLoading = isOpening || isFetching;

  // Reset form each time modal opens
  useEffect(() => {
    if (open) {
      setOpeningBalance('');
      setNotes('');
      setBalanceError('');
    }
  }, [open]);

  const validate = () => {
    const val = parseFloat(openingBalance);
    if (openingBalance === '' || isNaN(val)) {
      setBalanceError('Opening balance is required');
      return false;
    }
    if (val < 0) {
      setBalanceError('Opening balance cannot be negative');
      return false;
    }
    setBalanceError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      const res = await openSession({
        opening_balance: parseFloat(openingBalance),
        notes: notes.trim() || undefined,
      }).unwrap();
      onSessionOpened(res.data.session);
    } catch (err) {
      // 422 — terminal already has an open session, fetch and resume it
      if (err?.status === 422) {
        try {
          const listRes = await triggerListSessions({ per_page: 10 }).unwrap();
          const sessions = listRes?.data?.sessions || [];
          const existing = sessions.find((s) => s.status === 'open');
          if (existing) {
            showToast('Session already open — resuming existing session', 'info');
            onSessionOpened(existing);
            return;
          }
        } catch { /* fall through */ }
      }
      const msg = err?.data?.error || 'Failed to open session. Please try again.';
      showToast(msg, 'error');
    }
  };

  const handleBalanceChange = (e) => {
    const val = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(val)) {
      setOpeningBalance(val);
      if (balanceError) setBalanceError('');
    }
  };

  const dateStr = new Date().toLocaleDateString('en-IN', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  });

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '14px' },
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
          // Tighter margin on mobile so dialog doesn't feel oversized
          m: { xs: 2, sm: 4 },
          width: { xs: 'calc(100% - 32px)', sm: 'auto' },
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 1.75, sm: 2.5 },
          pb: 0,
          display: 'flex', alignItems: 'center', gap: 1.25,
        }}
      >
        <Box sx={{
          width: { xs: 30, sm: 36 }, height: { xs: 30, sm: 36 },
          borderRadius: '9px', bgcolor: '#EEF2FF',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <LockOpen sx={{ fontSize: { xs: 17, sm: 20 }, color: '#4F46E5' }} />
        </Box>
        <Box>
          <Typography fontWeight={800} fontSize={{ xs: '0.92rem', sm: '1rem' }} color="#1E293B" lineHeight={1.3}>
            Open Cash Session
          </Typography>
          <Typography fontSize={{ xs: '0.68rem', sm: '0.72rem' }} color="#94A3B8" fontWeight={500}>
            Enter drawer cash to begin your shift
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.5, sm: 2 }, pb: 0 }}>

        {/* Context: store · terminal · date */}
        <Box sx={{
          bgcolor: '#F8FAFC', border: '1px solid #F1F5F9',
          borderRadius: '8px',
          px: { xs: 1.25, sm: 1.5 },
          py: { xs: 0.75, sm: 1 },
          mb: { xs: 1.75, sm: 2.5 },
        }}>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Storefront sx={{ fontSize: 12, color: '#64748B' }} />
              <Typography fontSize={{ xs: '0.72rem', sm: '0.75rem' }} fontWeight={600} color="#1E293B" noWrap>
                {store?.name || 'Store'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Monitor sx={{ fontSize: 12, color: '#64748B' }} />
              <Typography fontSize={{ xs: '0.72rem', sm: '0.75rem' }} fontWeight={600} color="#1E293B" noWrap>
                {terminal?.terminal_number
                  ? `${terminal.terminal_number} · ${terminal.name}`
                  : terminal?.name || 'Terminal'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <CalendarToday sx={{ fontSize: 12, color: '#64748B' }} />
              <Typography fontSize={{ xs: '0.68rem', sm: '0.72rem' }} color="#64748B" noWrap>
                {dateStr}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Divider sx={{ mb: { xs: 1.75, sm: 2.5 } }} />

        {/* Opening balance */}
        <TextField
          fullWidth
          label="Opening Cash Balance"
          required
          value={openingBalance}
          onChange={handleBalanceChange}
          disabled={isLoading}
          error={!!balanceError}
          helperText={balanceError || ' '}
          inputProps={{ inputMode: 'decimal' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography sx={{ fontWeight: 700, color: '#64748B', fontSize: '0.9rem' }}>₹</Typography>
              </InputAdornment>
            ),
          }}
          sx={{ ...fieldSx, mb: 0.5 }}
        />

        {/* Notes (optional) */}
        <TextField
          fullWidth
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isLoading}
          placeholder="e.g. Morning shift"
          sx={{ ...fieldSx, mb: 0.5 }}
        />

      </DialogContent>

      <DialogActions sx={{
        px: { xs: 2, sm: 2.5 },
        pt: { xs: 1.25, sm: 2 },
        pb: { xs: 1.75, sm: 2.5 },
      }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || openingBalance === ''}
          disableElevation
          sx={{
            bgcolor: '#4F46E5', color: '#fff',
            py: { xs: 1, sm: 1.3 },
            fontSize: { xs: '0.85rem', sm: '0.9rem' },
            fontWeight: 700,
            borderRadius: '8px', textTransform: 'none',
            '&:hover': { bgcolor: '#4338CA' },
            '&.Mui-disabled': { bgcolor: '#C7D2FE', color: '#fff' },
          }}
        >
          {isLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={15} sx={{ color: '#fff' }} />
              <span>Starting Session...</span>
            </Stack>
          ) : (
            'Start Session'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpenSessionModal;
